// Preliminary sizing of simply supported glulam members under uniform load (ULS bending + shear, SLS deflection).
// Modes: generic beam, floor joist (solaio), pitched-roof rafter (copertura a falde, with NTC 2018 snow load).
// Units: kN, m in input; internally N and mm (1 kN/m = 1 N/mm).
export const GRADES = {
  GL24h: { fm: 24, fv: 3.5, E: 11500 },
  GL28h: { fm: 28, fv: 3.5, E: 12600 },
  GL32h: { fm: 32, fv: 3.5, E: 14200 },
};
export const CODES = {
  ntc: { label: "NTC 2018", gG: 1.3, gQ: 1.5, gM: 1.45 },
  ec5: { label: "Eurocodice 5", gG: 1.35, gQ: 1.5, gM: 1.25 },
};
// NTC 2018 §3.4.2 snow zones: [qsk up to 200 m, base, divisor]
export const SNOW_ZONES = {
  "I-A": [1.5, 1.39, 728],
  "I-M": [1.5, 1.35, 602],
  II: [1.0, 0.85, 481],
  III: [0.6, 0.51, 481],
};
const KMOD = 0.8; // medium-term load, service class 1-2 (conservative also for snow)
const KDEF = { 1: 0.6, 2: 0.8 };
const SELF = 5; // kN/m3 glulam self weight (conservative)
const KCR = 0.67;

export const snowLoad = (zone, alt) => {
  const [q200, base, div] = SNOW_ZONES[zone];
  return alt <= 200 ? q200 : base * (1 + (alt / div) ** 2);
};
export const snowShape = (deg) => (deg <= 30 ? 0.8 : deg >= 60 ? 0 : (0.8 * (60 - deg)) / 30);

// Variable load (kN/m² on horizontal projection) and its ψ2 for each mode
export const variableLoad = (p) => {
  if (p.mode !== "roof") return { Q: p.Q, psi2: p.mode === "floor" ? p.psi2 : 0.3 };
  const qs = snowShape(p.pitch) * snowLoad(p.zone, p.alt);
  return { Q: qs, psi2: p.alt > 1000 ? 0.2 : 0, qs };
};

export const checkSection = (p) => {
  const g = GRADES[p.grade];
  const c = CODES[p.code];
  const { Q, psi2 } = variableLoad(p);
  const cos = Math.cos(((p.mode === "roof" ? p.pitch : 0) * Math.PI) / 180);
  const Lmm = p.L * 1000; // horizontal span
  // loads per metre of horizontal projection; permanent G and self weight act on the sloped length
  const gk = (p.G * p.i + SELF * (p.b / 100) * (p.h / 100)) / cos;
  const qk = Q * p.i;
  const qd = c.gG * gk + c.gQ * qk;
  const M = (qd * Lmm * Lmm) / 8;
  const V = (qd * Lmm * cos) / 2;
  const B = p.b * 10;
  const H = p.h * 10;
  const I = (B * H ** 3) / 12;
  const sigma = M / ((B * H * H) / 6);
  const tau = (1.5 * V) / (KCR * B * H);
  const fmd = (KMOD * g.fm) / c.gM;
  const fvd = (KMOD * g.fv) / c.gM;
  const Ls = Lmm / cos; // member length
  const k = (5 * Lmm ** 4) / (384 * g.E * I * cos * cos);
  const wInst = k * (gk + qk);
  const wFin = k * (gk * (1 + KDEF[p.sc]) + qk * (1 + psi2 * KDEF[p.sc]));
  return { bending: sigma / fmd, shear: tau / fvd, inst: wInst / (Ls / 300), fin: wFin / (Ls / 250), wInst, wFin, Q, psi2 };
};

export const sizeBeam = (p) => {
  for (let h = 12; h <= 280; h += 4) {
    const r = checkSection({ ...p, h });
    if (Math.max(r.bending, r.shear, r.inst, r.fin) <= 1) return { h, ...r };
  }
  return null;
};
